/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import {
    bool,
    oneOf,
    func,
    number,
    shape,
    string,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import { IconButton, Popover } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ReactHtmlParser from 'react-html-parser';
import { useSelector } from 'react-redux';
import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import SkeletonStyles from '../GraphqlSkeletonComponents/SkeletonStyles';

const useStyles = makeStyles((theme) => ({
    ...SkeletonStyles,
    root: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: '6px',
        padding: '0',
        minWidth: 0,
        boxSizing: 'border-box',
        WebkitTapHighlightColor: 'transparent',
        [theme.breakpoints.up(1025)]: {
            padding: '2px 0',
        },
        '&:active': {
            backgroundColor: 'transparent !important',
        },
    },
    labelContainer: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        [theme.breakpoints.up(1025)]: {
            paddingRight: '6px',
        },
        '&:active, &:focus, &:focus-visible': {
            backgroundColor: 'transparent !important',
            outline: 'none',
        },
    },
    controlWrap: {
        width: '40px',
        height: '40px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        [theme.breakpoints.up(1025)]: {
            width: '36px',
            height: '36px',
        },
    },
    control: {
        padding: 0,
        color: '#000000',
        WebkitTapHighlightColor: 'transparent',
        backgroundColor: 'transparent !important',
        '&:hover': {
            backgroundColor: 'transparent !important',
        },
        '&.MuiButtonBase-root:active': {
            backgroundColor: 'transparent !important',
        },
        '&.Mui-focusVisible': {
            backgroundColor: 'transparent !important',
        },
        '& .MuiTouchRipple-root': {
            display: 'none !important',
        },
    },
    checkboxIcon: {
        width: '20px',
        height: '20px',
        border: '1px solid #000000',
        borderRadius: '2px',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
    },
    checkboxCheckedIcon: {
        width: '20px',
        height: '20px',
        border: '1px solid #000000',
        borderRadius: '2px',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            width: '6px',
            height: '11px',
            border: 'solid #000000',
            borderWidth: '0 2px 2px 0',
            left: '6px',
            top: '1px',
            transform: 'rotate(45deg)',
        },
    },
    radioIcon: {
        width: '20px',
        height: '20px',
        border: '1px solid #000000',
        borderRadius: '50%',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
    },
    radioCheckedIcon: {
        width: '20px',
        height: '20px',
        border: '1px solid #000000',
        borderRadius: '50%',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#000000',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        },
    },
    text: {
        fontSize: '16px',
        fontWeight: 300,
        lineHeight: '16px',
        color: '#000000',
        minWidth: 0,
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
        [theme.breakpoints.up(1025)]: {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
        },
    },
    count: {
        marginLeft: 'auto',
        fontSize: '16px',
        fontWeight: 300,
        lineHeight: '16px',
        color: '#7B7B7B',
        whiteSpace: 'nowrap',
        [theme.breakpoints.up(1025)]: {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '20px',
            paddingRight: '4px',
        },
    },
    tooltipIcon: {
        color: '#2F2F2F',
    },
    infoIconButton: {
        marginLeft: '4px',
    },
    infoContent: {
        padding: '8px',
        fontSize: '14px',
    },
    tooltipPopover: {
        backgroundColor: '#616161',
        color: '#fff',
        padding: theme.spacing(1),
        fontSize: 12,
        borderRadius: 4,
        maxWidth: 250,
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        width: '100%',
        height: '36px',
        paddingLeft: '10px',
        boxSizing: 'border-box',
        overflow: 'hidden',
    },
    loadingControl: {
        width: '20px',
        height: '20px',
        borderRadius: '4px',
        flexShrink: 0,
        backgroundColor: '#E9E9E9',
    },
    loadingLabel: {
        flex: 1,
        minWidth: 0,
        height: '14px',
        borderRadius: '4px',
        backgroundColor: '#E9E9E9',
    },
}));

const ProductFacetItem = ({
    entry,
    checked,
    onChange,
    type,
    className,
    loading,
}) => {
    const classes = useStyles();
    const isFiltersCountEnabled = useSelector(getFeatureFlag('is-filters-count-enabled'));
    const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
    const isRadio = type === 'radio';
    const controlId = `facet-item-${type}-${String(entry.value).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    const Control = isRadio ? (
        <Radio
            className={classes.control}
            checked={checked}
            onChange={(event) => onChange(entry.value, event.target.checked)}
            value={entry.value}
            color="default"
            disableRipple
            disableFocusRipple
            disableTouchRipple
            icon={<span className={classes.radioIcon} />}
            checkedIcon={<span className={classes.radioCheckedIcon} />}
            inputProps={{ id: controlId }}
            disabled={loading}
        />
    ) : (
        <Checkbox
            className={classes.control}
            checked={checked}
            onChange={(event) => onChange(entry.value, event.target.checked)}
            value={entry.value}
            color="default"
            disableRipple
            disableFocusRipple
            disableTouchRipple
            icon={<span className={classes.checkboxIcon} />}
            checkedIcon={<span className={classes.checkboxCheckedIcon} />}
            inputProps={{ id: controlId }}
        />
    );

    const handlePopoverOpen = (event) => {
        event.stopPropagation();
        setPopoverAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setPopoverAnchorEl(null);
    };

    return (
        <div className={`${classes.root} ${className || ''}`}>
            {(loading && !isRadio) ? (
                <div className={classes.loadingContainer} data-component="productFacetItemSkeleton">
                    <div className={`${classes.loadingControl} ${classes.shimmer}`} />
                    <div className={`${classes.loadingLabel} ${classes.shimmer}`} />
                </div>
            ) : (
                <>
                    <div className={classes.labelContainer}>
                        <span className={classes.controlWrap}>{Control}</span>
                        <label htmlFor={controlId} className={classes.text}>{entry.name}</label>
                        {entry.tooltip ? (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={handlePopoverOpen}
                                    onMouseDown={(event) => event.stopPropagation()}
                                    className={classes.infoIconButton}
                                >
                                    <InfoOutlinedIcon fontSize="small" className={classes.tooltipIcon} />
                                </IconButton>
                                <Popover
                                    open={Boolean(popoverAnchorEl)}
                                    onClose={handlePopoverClose}
                                    anchorEl={popoverAnchorEl}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                    PaperProps={{
                                        className: classes.tooltipPopover,
                                        elevation: 0,
                                    }}
                                >
                                    <div className={classes.infoContent}>
                                        {ReactHtmlParser(entry.tooltip)}
                                    </div>
                                </Popover>
                            </>
                        ) : null}
                    </div>
                    {isFiltersCountEnabled && (entry.count !== null && entry.count !== undefined && entry.count !== 0) && (
                        <span className={classes.count}>{`(${entry.count})`}</span>
                    )}
                </>
            )}
        </div>
    );
};

ProductFacetItem.propTypes = {
    entry: shape({
        name: string.isRequired,
        value: string.isRequired,
        count: number,
        from: number,
        to: number,
        tooltip: string,
    }).isRequired,
    checked: bool,
    onChange: func,
    type: oneOf(['checkbox', 'radio']),
    className: string,
    loading: bool,
};

ProductFacetItem.defaultProps = {
    checked: false,
    onChange: () => {},
    type: 'checkbox',
    className: '',
    loading: false,
};

export default ProductFacetItem;
